import * as React from 'react';
import { isCurrentCultureRightToLeft } from 'owa-localize';
import {
    updateAssetSize,
    getAsset,
    snapToGrid,
    moveTimeStreamAssetToPosition,
} from 'owa-timestream-store';
import { useProjectId } from '../hooks/useProjectId';
import type { AssetId } from 'owa-timestream-types';
import { useViewStateSelectors } from '../hooks/useViewStateSelectors';
import { usePointerEvents } from 'owa-react-hooks/lib/usePointerEvents';
import { getAssetDescriptor } from '../assetsCache';
import {
    onAssetResizeStart,
    onAssetResizeEnd,
    isResizeHandlesShown,
    getResizingAsset,
    getAssetResizingParent,
} from 'owa-timestream-asset-sdk-resize';
import { DEFAULT_MIN_ASSET_WIDTH } from '../constants';

/**
 * These values can be global because we only have one drag at a time occurring, so no collisions,
 * and they don't need to be stored in state because they're initialized within the memoized onPointerDown, so value
 * is ready when they're used in the next callbacks
 */
let pointerDownX = 0;
let pointerDownY = 0;
let currentRefRect: ClientRect | undefined = undefined;
let initialTop = 0;
let initialLeft = 0;
let initialHeight = 0;
let initialWidth = 0;
let parentPositionLeft = 0;
let parentPositionTop = 0;
let currentHorizontalEdgeType: HorizontalEdgeType | undefined = undefined;
let currentVerticalEdgeType: VerticalEdgeType | undefined = undefined;

export enum HorizontalEdgeType {
    Top,
    Bottom,
}

export enum VerticalEdgeType {
    Left,
    Right,
}

export function useResizeableElementState(ref: React.RefObject<HTMLDivElement>, assetId: AssetId) {
    const usingPointerEvents = usePointerEvents();
    const { isSelectedAsset, getZoom, isCanvasLocked } = useViewStateSelectors();
    const zoom = getZoom();
    const projectId = useProjectId();
    const { type, payload, bounds: assignedBounds, children, parentId } = getAsset(
        projectId,
        assetId
    );

    const currentlyResizingAsset = getResizingAsset();
    const isChildResizing =
        children.length > 0 &&
        children.some(child => {
            return currentlyResizingAsset && child == currentlyResizingAsset;
        });
    const isSelected = isSelectedAsset(assetId);
    const descriptor = getAssetDescriptor(type);
    const {
        minWidth = DEFAULT_MIN_ASSET_WIDTH,
        minHeight = 0,
        maxWidth = Number.MAX_VALUE,
        maxHeight = Number.MAX_VALUE,
        defaultWidth,
        defaultHeight,
    } = descriptor?.assetResizeConfiguration?.(payload) || {};

    // If dimensions have been assigned by resizing, use those, otherwise use defaults
    const defaultOrAssignedWidth = assignedBounds.width || defaultWidth;
    const defaultOrAssignedHeight = assignedBounds.height || defaultHeight;

    // disable resize if configuration is missing, disabled explicitly, or item has a parent
    // if item has resizing children, resize is enabled but handles are not shown
    const resizeDisabled =
        isCanvasLocked() ||
        (children.length > 0 && !isChildResizing) ||
        !descriptor ||
        !descriptor.assetResizeConfiguration ||
        descriptor.assetResizeConfiguration(payload).resizeDisabled;
    React.useEffect(() => {
        if (!isSelected) {
            onAssetResizeEnd();
        }
    }, [projectId, assetId, isSelected]);

    React.useEffect(() => {
        // VSO 101401 - re-fetch zoom in this effect so changing zoom does not trigger this
        // effect. Otherwise, zoom changes would trigger this effect with it's updated zoom values
        // before currentRefRect would have a chance to update.
        const zoom = getZoom();

        // when min/max bounds change, if the asset size is outside bounds of min/max, set the size
        currentRefRect = ref.current?.getBoundingClientRect();
        if (currentRefRect) {
            const boundedWidth = Math.max(
                minWidth,
                Math.min(maxWidth, defaultOrAssignedWidth || currentRefRect.width / zoom)
            );
            const boundedHeight = Math.max(
                minHeight,
                Math.min(maxHeight, defaultOrAssignedHeight || currentRefRect.height / zoom)
            );

            let shouldUpdate = false;
            if (boundedWidth !== currentRefRect.width / zoom) {
                widthRef.current = boundedWidth;
                shouldUpdate = true;
            }

            if (boundedHeight !== currentRefRect.height / zoom) {
                heightRef.current = boundedHeight;
                shouldUpdate = true;
            }

            if (shouldUpdate) {
                forceUpdate();
            }
        }
    }, [
        ref.current,
        defaultOrAssignedWidth,
        defaultOrAssignedHeight,
        getZoom,
        minWidth,
        minHeight,
        maxWidth,
        maxHeight,
    ]);

    /**
     * We have both refs and a toggle state here the positioning attributes. The refs are used because the values
     * get updated in onPointerMove, while onPointerUp is effectively memoized at the time that onPointerDown gets called,
     * so that if they were state values, they'd be stale. However, updating refs doesn't rerender the component. We
     * could have state values for each of the refs here as well, but we don't really conditionally rerender on specific
     * value changes, we rerender when any of them change, so we can use a simple toggle to force the rerender when one
     * updates
     */
    const heightRef = React.useRef<number>(defaultOrAssignedHeight || 0);
    const widthRef = React.useRef<number>(defaultOrAssignedWidth || 0);
    const topRef = React.useRef<number | undefined>(undefined);
    const leftRef = React.useRef<number | undefined>(undefined);
    const [, setRenderState] = React.useState<Object>({});
    const forceUpdate = React.useCallback(() => setRenderState({}), []);

    // Note that all this functionality is very similar to useMousePosition, but is hook based rather than global action based.
    // In addition, we want the ability to have this override the useMousePosition so canvas doesn't start dragging
    const createPointerEvents = React.useCallback(
        (horizontalEdgeType?: HorizontalEdgeType, verticalEdgeType?: VerticalEdgeType) => {
            const onPointerDown = (e: React.MouseEvent<HTMLDivElement>) => {
                e.preventDefault();
                e.stopPropagation();

                if (verticalEdgeType !== undefined) {
                    // flip edge types for RTL
                    currentVerticalEdgeType = isCurrentCultureRightToLeft()
                        ? verticalEdgeType === VerticalEdgeType.Left
                            ? VerticalEdgeType.Right
                            : VerticalEdgeType.Left
                        : verticalEdgeType;
                }
                currentHorizontalEdgeType = horizontalEdgeType;
                currentRefRect = ref.current?.getBoundingClientRect();
                initialTop = ref.current?.offsetTop ?? 0;
                initialLeft = ref.current?.offsetLeft ?? 0;
                initialHeight = (currentRefRect?.height ?? 0) / zoom;
                initialWidth = (currentRefRect?.width ?? 0) / zoom;
                pointerDownX = e.pageX;
                pointerDownY = e.pageY;
                heightRef.current = initialHeight;
                widthRef.current = initialWidth;
                topRef.current = initialTop;
                leftRef.current = initialLeft;

                if (parentId) {
                    parentPositionTop = 0;
                    parentPositionLeft = 0;
                }

                onAssetResizeStart(projectId, assetId);

                document.addEventListener(
                    usingPointerEvents ? 'pointermove' : 'mousemove',
                    onPointerMove,
                    true
                );

                document.addEventListener(
                    usingPointerEvents ? 'pointerup' : 'mouseup',
                    onPointerUp,
                    true
                );
            };

            return usingPointerEvents ? { onPointerDown } : { onMouseDown: onPointerDown };
        },
        [projectId, assetId, minWidth, minHeight, maxWidth, maxHeight, zoom, usingPointerEvents]
    );

    const onPointerMove = (e: MouseEvent) => {
        let diffX = snapToGrid((e.pageX - pointerDownX) / zoom);
        let diffY = snapToGrid((e.pageY - pointerDownY) / zoom);

        let shouldUpdate = false;
        // as we drag upwards, update the y position and the height
        if (currentHorizontalEdgeType !== undefined && diffY !== 0 && currentRefRect?.height) {
            let calculatedHeight = 0;
            let calculatedTop = 0;
            if (currentHorizontalEdgeType === HorizontalEdgeType.Top) {
                calculatedHeight = currentRefRect?.height / zoom - diffY;
                calculatedTop = initialTop + diffY;
            } else {
                calculatedHeight = currentRefRect?.height / zoom + diffY;
                calculatedTop = initialTop;
            }

            const newHeight = Math.max(minHeight, Math.min(maxHeight, calculatedHeight));
            const newTop = Math.min(initialTop + initialHeight - minHeight, calculatedTop);
            if (heightRef.current !== newHeight || topRef.current !== newTop) {
                heightRef.current = newHeight;
                if (!parentId) {
                    topRef.current = newTop;
                }
                shouldUpdate = true;
            }
        }

        // set width and left
        if (currentVerticalEdgeType !== undefined && diffX !== 0 && currentRefRect?.width) {
            let calculatedWidth = 0;
            let calculatedLeft = 0;
            if (currentVerticalEdgeType === VerticalEdgeType.Left) {
                calculatedWidth = currentRefRect?.width / zoom - diffX;
                calculatedLeft = initialLeft + diffX;
            } else {
                calculatedWidth = currentRefRect?.width / zoom + diffX;
                calculatedLeft = initialLeft;
            }

            const newWidth = Math.max(minWidth, Math.min(maxWidth, calculatedWidth));
            const newLeft = Math.min(initialLeft + initialWidth - minWidth, calculatedLeft);
            if (widthRef.current !== newWidth || leftRef.current !== newLeft) {
                widthRef.current = newWidth;
                if (!parentId) {
                    leftRef.current = newLeft;
                }
                shouldUpdate = true;
            }
        }

        if (shouldUpdate) {
            forceUpdate();

            if (parentId) {
                const {
                    isResizingWidestChild,
                    widestChildSize,
                    initialPosition,
                } = getAssetResizingParent();
                const parentElement = document.getElementById(parentId)?.getBoundingClientRect();
                let newParentLeft = 0;
                let newParentTop = 0;
                // we only want the asset to move if we're resizing the largest child in a bucket
                // left resize
                if (
                    !!diffX &&
                    currentVerticalEdgeType == VerticalEdgeType.Left &&
                    isResizingWidestChild
                ) {
                    newParentLeft = Math.min(
                        initialPosition.x! + (widestChildSize ?? minWidth) - minWidth,
                        initialPosition.x! + diffX
                    );
                    moveTimeStreamAssetToPosition(
                        projectId,
                        parentId,
                        newParentLeft,
                        undefined /** y */,
                        true /**onlySetLocally */
                    );
                    parentPositionLeft = diffX;
                }
                if (
                    !!diffY &&
                    parentElement &&
                    currentHorizontalEdgeType == HorizontalEdgeType.Top
                ) {
                    newParentTop = Math.min(
                        initialPosition.y! + (parentElement.height / zoom ?? minHeight) - minWidth,
                        initialPosition.y! + diffY
                    );

                    parentPositionTop = diffY;
                    moveTimeStreamAssetToPosition(
                        projectId,
                        parentId,
                        undefined /** x */,
                        newParentTop,
                        true /**onlySetLocally */
                    );
                }
            }
        }
    };

    const onPointerUp = (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (
            initialLeft !== undefined &&
            initialTop !== undefined &&
            leftRef.current !== undefined &&
            topRef.current !== undefined
        ) {
            const { initialPosition } = getAssetResizingParent();
            updateAssetSize(
                projectId,
                assetId,
                widthRef.current,
                heightRef.current,
                leftRef.current,
                topRef.current
            );
            if (parentId && initialPosition.x !== null && initialPosition.y !== null) {
                moveTimeStreamAssetToPosition(
                    projectId,
                    parentId,
                    initialPosition.x + parentPositionLeft,
                    initialPosition.y + parentPositionTop
                );
            }
        }

        if (!isResizeHandlesShown()) {
            onAssetResizeEnd();
        }

        // undo the local changes so the store update can take effect
        topRef.current = undefined;
        leftRef.current = undefined;
        widthRef.current = 0;
        heightRef.current = 0;
        currentHorizontalEdgeType = undefined;
        currentVerticalEdgeType = undefined;

        document.removeEventListener(
            usingPointerEvents ? 'pointermove' : 'mousemove',
            onPointerMove,
            true
        );

        document.removeEventListener(
            usingPointerEvents ? 'pointerup' : 'mouseup',
            onPointerUp,
            true
        );
    };

    const positioningAttributes = {
        style: {
            height: heightRef.current || defaultOrAssignedHeight,
            maxHeight: 'none',
            width: widthRef.current || defaultOrAssignedWidth,
            maxWidth: 'none',
            ...(leftRef.current !== undefined ? { left: leftRef.current } : {}),
            ...(topRef.current !== undefined ? { top: topRef.current } : {}),
        },
    };

    const resizeOnlyAttributes = resizeDisabled
        ? {
              isSelected: false,
              isResizeHandlesShown: false,
              createPointerEvents: undefined,
          }
        : {
              isSelected,
              isResizeHandlesShown: isResizeHandlesShown(),
              createPointerEvents,
          };

    return {
        positioningAttributes,
        ...resizeOnlyAttributes,
    };
}
